import { Button, styled, useTheme } from '@mui/material';
import {
  ColumnFiltersState,
  SortingState,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { SearchInput } from '../../../components/SearchInput/SearchInput';
import { TableSkeletonLoader } from '../../../components/SkeletonLoader/Table.SkeletonLoader';
import { TableV1 } from '../../../components/Table/TableV1';
import { Typography } from '../../../components/Typography/Typography';
import { SelectItem } from '../../../types';
import { isSubmitter } from '../../../utils/roles';
import { useAssigmentUsersTableColumns } from '../hooks/useAssigmentUsersTableColumns';
import { useUsers } from '../../../queries/hooks/useUsers';
import { Multiselect } from '../../../components/Multiselect/Multiselect';
import { isSmallScreenState } from '../../../state/UIState';
import { useAtomValue } from 'jotai';

const Wrapper = styled('div')``;

const FiltersSection = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 20px;
  gap: 12px;
`;

export const AssigmentUserTable = () => {
  const { isLoading: areUsersLoading, data: users } = useUsers();
  const { colors } = useTheme();
  const columns = useAssigmentUsersTableColumns();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [selectedOPs, setSelectedOPs] = useState<SelectItem[]>([]);
  const isSmallScreen = useAtomValue(isSmallScreenState);

  const operatingManagers = useMemo(() => {
    if (!users) return [];
    return users.filter((user) => isSubmitter(user));
  }, [users]);

  const table = useReactTable({
    data: operatingManagers,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    enableSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getRowId: (user) => String(user.id),
  });

  const allOPs = useMemo(
    () => operatingManagers.sort().map((user) => ({ id: user.id, value: user.name })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operatingManagers]
  );

  const onChangeOPFilter = useCallback(
    (OPs: SelectItem[] | undefined) => {
      if (OPs) {
        setSelectedOPs(OPs);
        table.getColumn('name')?.setFilterValue(OPs);
      }
    },
    [setSelectedOPs, table]
  );

  const isResetDisabled = useMemo(() => {
    const selectedOPsFilter = columnFilters.find((filter) => filter.id === 'name')
      ?.value as SelectItem[];
    const searchFilter = columnFilters.find((filter) => filter.id === 'operatingPartnerDeals')
      ?.value as string;
    if (!searchFilter && (selectedOPsFilter ?? []).length === 0) return true;
    return false;
  }, [columnFilters]);

  const onFiltersReset = useCallback(() => {
    setSelectedOPs([]);
    table.getColumn('name')?.setFilterValue([]);
    table.getColumn('operatingPartnerDeals')?.setFilterValue('');
  }, [table]);

  if (areUsersLoading || !users) return <TableSkeletonLoader />;

  return (
    <Wrapper>
      <FiltersSection>
        <SearchInput
          placeholder='Search'
          style={{ width: isSmallScreen ? '150px' : '370px', height: '36px' }}
          onClear={() => table.getColumn('operatingPartnerDeals')?.setFilterValue('')}
          onChange={(e) => table.getColumn('operatingPartnerDeals')?.setFilterValue(e.target.value)}
          value={table.getColumn('operatingPartnerDeals')?.getFilterValue() ?? ''}
        />
        <Multiselect
          style={{  width: isSmallScreen ? '150px' : '200px', marginRight: '14px' }}
          fieldPlaceholder={isSmallScreen ? 'Submitter' : 'Select submitter'}
          options={allOPs}
          value={selectedOPs}
          onChange={(_, OPs) => onChangeOPFilter(OPs)}
        />
        <Button
          onClick={onFiltersReset}
          variant='text'
          style={{ height: '28px', marginTop: '2px' }}
          disabled={isResetDisabled}
          sx={{
            '.MuiButton-startIcon': { marginRight: '2px' },
            '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'auto' },
          }}
        >
          <Typography variant='subtitle1' color={colors.textAccent.default}>
            Reset Filters
          </Typography>
        </Button>
      </FiltersSection>
      <TableV1
        table={table}
        height='auto'
        wrapperStyle={{
          maxHeight: 'calc(100vh - 288px)',
          overflowX: 'scroll',
          border: `1px solid ${colors.border.default}`,
        }}
      />
    </Wrapper>
  );
};
